'use client'

import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useState } from 'react'
import { Activity } from 'lucide-react'
import posthog from 'posthog-js'
import { signUpFromInvitation } from '@/lib/actions/invite-signup'

export default function SignUpPage() {
  return (
    <Suspense>
      <SignUpForm />
    </Suspense>
  )
}

function SignUpForm() {
  const searchParams = useSearchParams()
  // Arriving from an invite link. This takes a different path entirely: the
  // account is created server-side, pre-confirmed, and joined to the inviting
  // practice — see lib/actions/invite-signup.ts.
  const invitationToken = searchParams.get('invite')
  const invitedEmail = searchParams.get('email')

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState(invitedEmail ?? '')
  const [password, setPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    const supabase = createClient()
    setIsLoading(true)
    setError(null)

    if (password !== repeatPassword) {
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    // Invited sign-ups skip the confirmation email: the invitation link was
    // only ever sent to this address, so the mailbox is already proven and a
    // second link would send them back to the same inbox for nothing. The
    // server action re-checks the token and takes the address from the
    // invitation, not from this form.
    if (invitationToken) {
      try {
        const result = await signUpFromInvitation({
          token: invitationToken,
          fullName,
          password,
        })
        if (result.error) {
          setError(result.error)
          return
        }
        posthog.capture('user_signed_up', { from_invitation: true })
        router.push('/auth/onboarding')
        router.refresh()
      } catch (error: unknown) {
        posthog.captureException(error)
        setError(error instanceof Error ? error.message : 'An error occurred')
      } finally {
        setIsLoading(false)
      }
      return
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          // Environment-correct by construction (localhost / dev.ponelat.com /
          // app.stridenotes.co.za). NOTE: the URL must be on the Supabase
          // project's redirect allow-list, or GoTrue swaps in the Site URL.
          // /auth/confirm exchanges the emailed code for a session server-side,
          // then forwards to onboarding — so the user arrives authenticated.
          emailRedirectTo: `${window.location.origin}/auth/confirm?next=/auth/onboarding`,
          data: {
            full_name: fullName,
          },
        },
      })
      if (error) throw error
      // With email confirmation required (the project default), signUp returns
      // no session — the user isn't authenticated until they click the emailed
      // link, so onboarding's profile save would fail. Only go straight to
      // onboarding when a session actually exists (autoconfirm).
      posthog.capture('user_signed_up')
      router.push(data.session ? '/auth/onboarding' : '/auth/sign-up-success')
    } catch (error: unknown) {
      posthog.captureException(error)
      setError(error instanceof Error ? error.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center bg-background p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-center">
            <img src="/logo.svg" alt="StrideNotes" className="h-24 w-auto" />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Create your account</CardTitle>
              <CardDescription>
                {invitationToken
                  ? "Pick a password and you're straight in — no confirmation email, your invitation already proved this address is yours."
                  : 'Start managing your practice today'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignUp}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Jane Smith"
                      required
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      // The invitation is bound to this address; signing up
                      // with another one would just fail to join.
                      readOnly={Boolean(invitedEmail)}
                      aria-readonly={Boolean(invitedEmail)}
                    />
                    {invitedEmail && (
                      <p className="text-muted-foreground text-xs">
                        Your invitation was sent to this address.
                      </p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="repeat-password">Confirm Password</Label>
                    <Input
                      id="repeat-password"
                      type="password"
                      required
                      value={repeatPassword}
                      onChange={(e) => setRepeatPassword(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Creating account...' : 'Create account'}
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm">
                  Already have an account?{' '}
                  <Link
                    href="/auth/login"
                    className="underline underline-offset-4 text-primary"
                  >
                    Sign in
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
